import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  LabelValueSource,
  LabelType,
  LabelValueAcceptedBy
} from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks';
import { DependantLabelData, DependantLabels } from '../components/schema';

const isLabelASuggestion = (dependentLabel?: DependantLabelData): boolean =>
  (dependentLabel?.label?.value?.idValue &&
    dependentLabel?.label.valueSource === LabelValueSource.Suggestion) ||
  false;

const setLabelValueSourceToUser = (
  label: DependantLabelData,
  setValue: UseFormReturn['setValue'],
  timesheetLabelsFormField: string,
  isLabelLevelAcceptedByEnabled: boolean
) => {
  setValue(
    `${timesheetLabelsFormField}.${label.index}.label`,
    isLabelLevelAcceptedByEnabled
      ? {
          ...label.label,
          acceptedBy: LabelValueAcceptedBy.User,
          valueSource: label.label.valueSource ?? LabelValueSource.User
        }
      : {
          ...label.label,
          valueSource: LabelValueSource.User
        },
    { shouldValidate: true, shouldDirty: true }
  );
};

export const useUpdateDependentLabelsValueSourceOnSelection = (
  dependentLabels: DependantLabels,
  type: LabelType,
  timesheetLabelsFormField: string,
  setValue: UseFormReturn['setValue']
): (() => void) => {
  const isLabelLevelAcceptedByEnabled = useFeatureFlag(
    'isLabelLevelAcceptedByEnabled',
    false
  );
  return useCallback(() => {
    switch (type) {
      case LabelType.Billing:
        if (
          dependentLabels.task.label.value?.stringValue &&
          dependentLabels.task.label.valueSource === LabelValueSource.Suggestion
        ) {
          setLabelValueSourceToUser(
            dependentLabels.task,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        if (isLabelASuggestion(dependentLabels.project)) {
          setLabelValueSourceToUser(
            dependentLabels.project,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        if (
          isLabelASuggestion(dependentLabels?.client) &&
          dependentLabels?.client
        ) {
          setLabelValueSourceToUser(
            dependentLabels.client,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        if (
          isLabelASuggestion(dependentLabels?.program) &&
          dependentLabels?.program
        ) {
          setLabelValueSourceToUser(
            dependentLabels.program,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        break;
      case LabelType.Task:
        if (isLabelASuggestion(dependentLabels.project)) {
          setLabelValueSourceToUser(
            dependentLabels.project,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        if (
          isLabelASuggestion(dependentLabels.client) &&
          dependentLabels.client
        ) {
          setLabelValueSourceToUser(
            dependentLabels.client,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        if (
          isLabelASuggestion(dependentLabels?.program) &&
          dependentLabels?.program
        ) {
          setLabelValueSourceToUser(
            dependentLabels.program,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        break;
      case LabelType.Project:
        if (
          isLabelASuggestion(dependentLabels.client) &&
          dependentLabels.client
        ) {
          setLabelValueSourceToUser(
            dependentLabels.client,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }
        if (
          isLabelASuggestion(dependentLabels?.program) &&
          dependentLabels?.program
        ) {
          setLabelValueSourceToUser(
            dependentLabels.program,
            setValue,
            timesheetLabelsFormField,
            isLabelLevelAcceptedByEnabled
          );
        }

        break;
    }
  }, [
    dependentLabels.client,
    dependentLabels.program,
    dependentLabels.project,
    dependentLabels.task,
    setValue,
    isLabelLevelAcceptedByEnabled,
    timesheetLabelsFormField,
    type
  ]);
};
